/**
 * Person properties captured while the visitor is still anonymous.
 *
 * PostHog runs with `person_profiles: 'identified_only'`, so an anonymous
 * visitor has no person and the server drops `$set_once` on their events. The
 * first search usually happens before sign-up, so without this it would never
 * reach the person. We hold the values in the browser and hand them to
 * `posthog.identify` (as `$set_once`) when the visitor becomes a user.
 *
 * First write wins per key, matching `$set_once`. Storage can be missing or
 * throw (private mode, blocked site data); every access is guarded and a
 * failure only means the values are not carried over.
 */
const STORAGE_KEY = 'dupe_pending_person_set_once'

const read = (): Record<string, unknown> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export const stashPendingPersonSetOnce = (
  props: Record<string, unknown>
): void => {
  try {
    const current = read()
    const merged = { ...props, ...current }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged))
  } catch {
    // Storage unavailable: nothing to carry over.
  }
}

/** Returns the held values and clears them, or undefined when there are none. */
export const takePendingPersonSetOnce = ():
  | Record<string, unknown>
  | undefined => {
  const current = read()
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore.
  }
  return Object.keys(current).length > 0 ? current : undefined
}
